/* eslint-disable max-len */
/* eslint-disable react/prop-types */

import {
  Button,
  Col,
  Form,
  Input,
  Modal,
  Row,
  Spin,
} from 'antd';
import React, { useEffect, useState } from 'react';

import {
  deleteMotivoBajaNombramiento,
  getMotivoBajaNombramiento,
  getMotivosBajaNombramientos,
  patchMotivoBajaNombramiento,
  postMotivoBajaNombramiento,
} from 'api/catalogos/MotivosBajaNombramientos';
import Select from 'components/Select';
import Table from 'components/Table';
import { formatReceived } from 'utils/formatValues';
import { CheckCircleOutlined } from '@ant-design/icons';
import {
  ESTATUS_ACTIVO,
  ESTATUS_AUTORIZADO,
  ESTATUS_CANCELADO,
  ESTATUS_INACTIVO,
} from 'utils/constants';

import { getEstadosGlobales } from 'api/catalogos/index';
import { checkToken } from 'api/utils';
import FormSubmitControls from 'components/FormSubmitControls';
import ModalDelete from 'components/ModalDelete';
import Moment from 'moment';
import { onError, onSuccess } from 'utils/handlers';
import ModalConfirmation from 'components/ModalConfirmation';

export const motivosBajaNombramientosPermission = {
  permissionModel: 'motivodebajadenombramiento',
};

const MotivosBajaDeNombramiento = ({ permission }) => {
  // ============================ Estados y formularios ============================
  const [form] = Form.useForm();
  const [formDatos] = Form.useForm();
  const [formInstances] = useState([form, formDatos]);

  const [open, setVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [visibleAlert, setVisibleAlert] = useState(false);
  const [confirmationVisible, setConfirmationVisible] = useState(false);

  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [data, setData] = useState([]);
  const [estados, setEstados] = useState([]);

  // ============================ Funciones de carga de datos ============================

  // Carga general de motivos de baja (usado después de crear, editar o borrar)
  const fetchData = async () => {
    setLoading(true);
    const _data = await getMotivosBajaNombramientos();
    setData(formatReceived(_data));
    setLoading(false);
  };

  // useEffect inicial para cargar catálogos y motivos
  useEffect(() => {
    let mounted = true;
    const fetchAll = async () => {
      setLoading(true);
      await checkToken();
      const [_estados, _motivosbaja] = await Promise.all([
        getEstadosGlobales(),
        getMotivosBajaNombramientos(),
      ]);
      if (mounted) {
        setEstados(_estados);
        setData(formatReceived(_motivosbaja));
        setLoading(false);
      }
    };
    fetchAll();
    return () => { mounted = false; };
  }, []);

  // ============================ Configuración de tabla ============================

  // Configuración de columnas de la tabla
  const columns = [
    {
      titleText: 'Motivo',
      dataIndex: 'descripcion',
      key: 'descripcion',
      width: 400,
    },
    {
      titleText: 'Estatus',
      dataIndex: 'estados_globales',
      key: 'estados_globales',
      width: 200,
      render: (val) => estados.find((e) => e.id === val)?.descripcion,
    },
  ];

  // Función para manejar el clic en una fila de la tabla
  const handleOnRowClick = (record) => {
    setSelectedRowKeys([record.id]);
    form.setFieldsValue(record);
  };

  // Función para manejar el tipo de selección de filas
  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
  };

  // ============================ Funciones de UI ============================

  // Función para cerrar el modal y limpiar los formularios
  const onCancel = () => {
    setSelectedRowKeys([]);
    setVisible(false);
    setIsEditing(false);
    form.resetFields();
    formDatos.resetFields();
  };

  // Función para abrir el modal de agregar
  const onClickAdd = () => {
    onCancel();
    setVisible(true);
  };

  // Función para abrir el modal de eliminar
  const onClickDelete = () => {
    setVisibleAlert(true);
  };

  // Función para abrir el modal de editar
  const onClickEdit = async (responseData = null) => {
    setLoading(true);
    const [key] = selectedRowKeys;
    const match = responseData || data.find((e) => e.id === key);
    const _motivoBaja = await getMotivoBajaNombramiento(match.id);

    if (_motivoBaja) {
      form.setFieldsValue({
        ...formatReceived(_motivoBaja),
        vigencia_inicial: _motivoBaja.vigencia_inicial
          ? new Moment(_motivoBaja.vigencia_inicial, 'YYYY-MM-DD')
          : null,
      });
      formDatos.setFieldsValue(formatReceived(_motivoBaja));
      setVisible(true);
      setIsEditing(true);
    }
    setLoading(false);
  };

  // ============================ CRUD: Guardar y Eliminar ============================

  const onFinish = async (_values, _continue = false) => {
    try {
      setLoading(true);
      await form.validateFields();
      await formDatos.validateFields();

      const rawValues = {
        ...form.getFieldsValue(),
        ...formDatos.getFieldsValue(),
      };
      const values = {
        ...rawValues,
        vigencia_inicial: rawValues.vigencia_inicial?.format?.('YYYY-MM-DD') || Moment().format('YYYY-MM-DD'),
      };

      const [key] = selectedRowKeys;
      const saved = await (key
        ? patchMotivoBajaNombramiento(key, values)
        : postMotivoBajaNombramiento(values));

      if (saved) {
        if (_continue) {
          await fetchData();
          setSelectedRowKeys([saved.id]);
          onClickEdit(saved);
        } else {
          onCancel();
          await fetchData();
        }
      }
      setLoading(false);
      setIsEditing(false);
    } catch (err) {
      onError(err, setLoading, formInstances);
    }
  };

  const handleOnFinishConfirmation = async () => {
    try {
      setLoading(true);
      const [key] = selectedRowKeys;
      if (!key) {
        setLoading(false);
        onCancel();
        onSuccess({}, 'No se puede autorizar una baja ya autorizada');
        return;
      }
      const selectedItem = data.find((item) => item.id === key);
      const vals = {
        ...form.getFieldValue(),
        estados_globales: ESTATUS_AUTORIZADO,
        vigencia_inicial: Moment(selectedItem?.vigencia_inicial).format('YYYY-MM-DD') || Moment().format('YYYY-MM-DD'),
      };
      const res = await patchMotivoBajaNombramiento(key, vals);
      if (res) {
        onSuccess(res);
        await fetchData();
        setConfirmationVisible(false);
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const deleteItem = async () => {
    try {
      setLoading(true);
      if (selectedRowKeys.length) {
        const [key] = selectedRowKeys;
        const deleted = await deleteMotivoBajaNombramiento(key);
        if (deleted) {
          setVisibleAlert(false);
          onCancel();
          await fetchData();
        }
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  // ============================ Validación ============================

  const requiredRule = {
    required: true,
    message: 'Este campo es obligatorio',
  };

  const rules = {
    required: [requiredRule],
    concepto: [requiredRule],
  };

  // ============================ Render ============================

  return (
    <Row className="container">
      <Spin tip="Cargando..." spinning={loading}>
        {/* Tabla de motivos */}
        <Table
          cols={columns}
          data={data}
          rowSelection={rowSelection}
          handleOnRowClick={handleOnRowClick}
          permission={permission}
          allowExportPDF={false}
          controls={{
            onClickAdd,
            onClickEdit: () => onClickEdit(),
            onClickDelete,
          }}
          customActions={[
            {
              onClick: () => setConfirmationVisible(true),
              permission: { model: 'motivodebajadenombramiento', action: 'autorizar' },
              text: 'Autorizar',
              icon: <CheckCircleOutlined />,
              disabledBySelection: false,
              className: 'success',
              disabled: !selectedRowKeys?.length
                || data?.find((e) => e?.id === selectedRowKeys[0])?.estados_globales === ESTATUS_AUTORIZADO
                || data?.find((e) => e?.id === selectedRowKeys[0])?.estados_globales === ESTATUS_CANCELADO
                || data?.find((e) => e?.id === selectedRowKeys[0])?.estados_globales === ESTATUS_INACTIVO,
            },
          ]}
        />

        {/* Modal Agregar/Editar */}
        <Modal
          open={open}
          width="55%"
          closable={false}
          maskClosable={!loading}
          keyboard={!loading}
          footer={null}
          title={(
            <FormSubmitControls
              label={`${selectedRowKeys.length ? 'Editar' : 'Agregar'} Motivos de Baja de Nombramiento`}
              onFinish={onFinish}
              onCancel={onCancel}
              loading={loading}
            />
          )}
          onCancel={onCancel}
          forceRender
        >
          <Form
            form={form}
            layout="vertical"
            name="form-motivos-baja-nombramientos"
            initialValues={{
              estados_globales: ESTATUS_ACTIVO,
              is_active: true,
              vigencia_inicial: Moment(),
            }}
          >
            <Row>
              <Col xs={24}>
                <Form.Item
                  name="descripcion"
                  label="Descripción"
                  hasFeedback
                  rules={rules.required}
                >
                  <Input dataSource={data} />
                </Form.Item>
              </Col>
              <Col xs={24}>
                <Form.Item
                  name="estados_globales"
                  label="Estatus"
                  rules={rules.required}
                  hasFeedback
                >
                  <Select
                    labelProp="descripcion"
                    keyProp="id"
                    dataSource={estados}
                    disabled={!isEditing}
                  />
                </Form.Item>
              </Col>
              <Form.Item hidden>
                <Button htmlType="submit" />
              </Form.Item>
            </Row>
          </Form>

          <Form
            form={formDatos}
            layout="vertical"
            name="form-motivos-baja-nombramientos-datos"
          >
            <Form.Item name="is_active" hidden>
              <input type="hidden" />
            </Form.Item>
          </Form>
        </Modal>

        {/* Modal de confirmación para eliminar */}
        <ModalDelete
          onDelete={deleteItem}
          onCancel={() => setVisibleAlert(false)}
          open={visibleAlert}
          content="El Registro Seleccionado"
          loading={loading}
        />
        <ModalConfirmation
          onFinish={handleOnFinishConfirmation}
          ConfirmAutorize
          onCancel={() => {
            setConfirmationVisible(false);
          }}
          open={confirmationVisible}
          contentTitle={`Autorizar es Motivos de Baja ${form.getFieldValue('descripcion')}`}
          loading={loading}
        />
      </Spin>
    </Row>
  );
};

export default MotivosBajaDeNombramiento;
